import React, { useEffect, useState, useRef } from "react";
import TransactionReportGenerator from "./TransactionReportGenerator";
import { adminUserService } from "../../../services/api/admin.user.service";
import { notificationService } from "../../../services/api/notification.service";
import { debounce } from "lodash";

const TransactionList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newTransactionIds, setNewTransactionIds] = useState(new Set());

  // Load acknowledged transaction IDs from localStorage on component mount
  const [acknowledgedTransactionIds, setAcknowledgedTransactionIds] = useState(
    () => {
      try {
        const stored = localStorage.getItem("acknowledgedTransactionIds");
        return stored ? new Set(JSON.parse(stored)) : new Set();
      } catch (error) {
        console.error("Error loading acknowledged transaction IDs:", error);
        return new Set();
      }
    }
  );

  const [loadingStates, setLoadingStates] = useState({ markAsRead: false });

  // Fixed Pagination state with proper initialization
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  // Filter dropdown state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  // Transaction status and type options
  const statusOptions = ["All", "Pending", "Completed", "Failed", "Cancelled"];
  const typeOptions = [
    "All",
    "Credit",
    "Debit",
    "Refund",
    "Withdrawal",
    "Deposit",
  ];

  // Function to persist acknowledged transaction IDs to localStorage
  const persistAcknowledgedIds = (acknowledgedIds) => {
    try {
      localStorage.setItem(
        "acknowledgedTransactionIds",
        JSON.stringify([...acknowledgedIds])
      );
    } catch (error) {
      console.error("Error persisting acknowledged transaction IDs:", error);
    }
  };

  // Function to clean up old acknowledged IDs (older than 7 days)
  const cleanupOldAcknowledgedIds = () => {
    try {
      const stored = localStorage.getItem("acknowledgedTransactionIds");
      const storedTimestamp = localStorage.getItem(
        "acknowledgedTransactionIds_timestamp"
      );

      if (stored && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp);
        const now = Date.now();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

        // If data is older than 7 days, clear it
        if (now - timestamp > sevenDaysInMs) {
          localStorage.removeItem("acknowledgedTransactionIds");
          localStorage.removeItem("acknowledgedTransactionIds_timestamp");
          setAcknowledgedTransactionIds(new Set());
        }
      } else if (stored) {
        // Set timestamp if it doesn't exist
        localStorage.setItem(
          "acknowledgedTransactionIds_timestamp",
          Date.now().toString()
        );
      }
    } catch (error) {
      console.error("Error during cleanup:", error);
    }
  };

  // Function to check if transaction is new (within last 24 hours)
  const isNewTransaction = (timestamp) => {
    const transactionDate = new Date(timestamp);
    const now = new Date();
    const timeDiff = now.getTime() - transactionDate.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    return hoursDiff <= 24;
  };

  // Function to check if transaction should show blue dot (new and not acknowledged)
  const shouldShowBlueDot = (transactionId, timestamp) => {
    return (
      isNewTransaction(timestamp) &&
      !acknowledgedTransactionIds.has(transactionId)
    );
  };

  // Function to get unacknowledged new transactions count
  const getUnacknowledgedNewCount = () => {
    return Array.from(newTransactionIds).filter(
      (id) => !acknowledgedTransactionIds.has(id)
    ).length;
  };

  // Enhanced function to handle transaction click (similar to admin notifications)
  const handleTransactionClick = async (transaction) => {
    const transactionId = transaction._id;
    const timestamp = transaction.timestamp;

    // Mark as read if it's a new unacknowledged transaction
    if (shouldShowBlueDot(transactionId, timestamp)) {
      try {
        // Call the notification service to mark this specific transaction notification as read
        await notificationService.markAsRead({
          notificationIds: [transactionId],
          category: "Transaction",
        });

        // Update local state and persist to localStorage
        const newAcknowledgedIds = new Set([
          ...acknowledgedTransactionIds,
          transactionId,
        ]);
        setAcknowledgedTransactionIds(newAcknowledgedIds);
        persistAcknowledgedIds(newAcknowledgedIds);
      } catch (error) {
        console.error("Error marking transaction notification as read:", error);
        // Still update local state to prevent UI issues
        const newAcknowledgedIds = new Set([
          ...acknowledgedTransactionIds,
          transactionId,
        ]);
        setAcknowledgedTransactionIds(newAcknowledgedIds);
        persistAcknowledgedIds(newAcknowledgedIds);
      }
    }
  };

  // Enhanced function to acknowledge (mark as viewed) a single transaction
  const acknowledgeTransaction = async (transactionId) => {
    try {
      // Mark specific transaction notification as read in the backend
      await notificationService.markAsRead({
        notificationIds: [transactionId],
        category: "Transaction",
      });

      // Update local state and persist to localStorage
      const newAcknowledgedIds = new Set([
        ...acknowledgedTransactionIds,
        transactionId,
      ]);
      setAcknowledgedTransactionIds(newAcknowledgedIds);
      persistAcknowledgedIds(newAcknowledgedIds);
    } catch (error) {
      // Still update local state even if API call fails to prevent UI issues
      const newAcknowledgedIds = new Set([
        ...acknowledgedTransactionIds,
        transactionId,
      ]);
      setAcknowledgedTransactionIds(newAcknowledgedIds);
      persistAcknowledgedIds(newAcknowledgedIds);
    }
  };

  // Enhanced function to mark all transaction notifications as read
  const handleMarkAsRead = async () => {
    const unacknowledgedCount = getUnacknowledgedNewCount();
    if (unacknowledgedCount === 0) return;

    setLoadingStates((prev) => ({ ...prev, markAsRead: true }));

    try {
      // Get all unacknowledged new transaction IDs
      const unacknowledgedIds = Array.from(newTransactionIds).filter(
        (id) => !acknowledgedTransactionIds.has(id)
      );

      // Method 1: Mark the entire Transaction category as read
      try {
        await notificationService.markCategoryAsRead("Transaction");
      } catch (categoryError) {
        console.error(
          "Error marking Transaction category as read:",
          categoryError
        );
      }

      // Method 2: Mark individual transaction notifications as read
      if (unacknowledgedIds.length > 0) {
        try {
          await notificationService.markAsRead({
            notificationIds: unacknowledgedIds,
            category: "Transaction",
          });
        } catch (individualError) {
          console.error(
            "Error marking individual transaction notifications as read:",
            individualError
          );
        }
      }

      // Method 3: Bulk mark all new transaction notifications
      try {
        await notificationService.markAsRead({
          category: "Transaction",
          filters: {
            isNew: true,
            timestamp: {
              $gte: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
            },
          },
        });
      } catch (bulkError) {
        console.error("Error in bulk marking:", bulkError);
      }

      // Update local state - mark all new transactions as acknowledged and persist
      const newAcknowledgedIds = new Set([...acknowledgedTransactionIds]);
      unacknowledgedIds.forEach((id) => newAcknowledgedIds.add(id));
      setAcknowledgedTransactionIds(newAcknowledgedIds);
      persistAcknowledgedIds(newAcknowledgedIds);
    } catch (error) {
      console.error("Failed to mark transaction notifications as read:", error);
      // Show user-friendly error message
      alert("Failed to mark notifications as read. Please try again.");
    } finally {
      setLoadingStates((prev) => ({ ...prev, markAsRead: false }));
    }
  };

  // Function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  // Function to get type badge color
  const getTypeBadgeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "credit":
      case "deposit":
        return "bg-green-100 text-green-800";
      case "debit":
      case "withdrawal":
        return "bg-red-100 text-red-800";
      case "refund":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-purple-100 text-purple-800";
    }
  };

  // Fixed fetchTransactions function with proper pagination handling
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim() || undefined,
        status: status && status !== "All" ? status : undefined,
        type: type && type !== "All" ? type : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        minAmount: minAmount || undefined,
        maxAmount: maxAmount || undefined,
      };

      // Remove undefined keys
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key]
      );

      const response = await adminUserService.getTransactions(params);

      if (response && response.success && response.data) {
        const { transactions: transactionData, pagination: paginationData } =
          response.data;

        const currentTransactions = transactionData || [];

        // Check for new transactions and update the newTransactionIds set
        const newIds = new Set();
        currentTransactions.forEach((txn) => {
          if (isNewTransaction(txn.timestamp)) {
            newIds.add(txn._id);
          }
        });
        setNewTransactionIds(newIds);

        setTransactions(currentTransactions);

        // Fixed pagination state update
        setPagination((prev) => ({
          ...prev,
          page:
            paginationData?.currentPage || paginationData?.page || prev.page,
          totalPages:
            paginationData?.totalPages ||
            paginationData?.pages ||
            Math.ceil(
              (paginationData?.totalItems || paginationData?.total || 0) /
                prev.limit
            ),
          total: paginationData?.totalItems || paginationData?.total || 0,
        }));
      } else {
        setTransactions([]);
        setError("Invalid response format");
        setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
      }
    } catch (error) {
      setTransactions([]);
      setError(error.message || "Failed to fetch transactions");
      setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Cleanup old acknowledged IDs on component mount
  useEffect(() => {
    cleanupOldAcknowledgedIds();
  }, []);

  // FIXED: Create debounced search function with proper cleanup
  const debouncedSearch = useRef(
    debounce((query) => {
      setSearch(query);
      setPagination((prev) => ({
        ...prev,
        page: 1,
      }));
    }, 500)
  ).current;

  // FIXED: Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // FIXED: Handle search input change properly
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // FIXED: useEffect with proper dependency management
  useEffect(() => {
    fetchTransactions();
  }, [
    pagination.page,
    pagination.limit,
    search,
    status,
    type,
    startDate,
    endDate,
    minAmount,
    maxAmount,
  ]);

  // Auto-refresh transactions every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTransactions();
    }, 30000);

    return () => clearInterval(interval);
  }, [
    pagination.page,
    pagination.limit,
    search,
    status,
    type,
    startDate,
    endDate,
    minAmount,
    maxAmount,
  ]);

  // Fixed handlePageChange function
  const handlePageChange = (newPage) => {
    if (
      newPage >= 1 &&
      newPage <= pagination.totalPages &&
      newPage !== pagination.page
    ) {
      setPagination((prev) => ({
        ...prev,
        page: newPage,
      }));
    }
  };

  // Fixed handleLimitChange function
  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(newLimit),
      page: 1, // Reset to first page when changing limit
    }));
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setStatus("");
    setType("");
    setStartDate("");
    setEndDate("");
    setMinAmount("");
    setMaxAmount("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    setShowFilterDropdown(false);
  };

  // Apply filters
  const handleApplyFilters = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    setShowFilterDropdown(false);
  };

  // Toggle filter dropdown
  const toggleFilterDropdown = () => {
    setShowFilterDropdown(!showFilterDropdown);
  };

  // Count active filters
  const countActiveFilters = () => {
    let count = 0;
    if (status && status !== "All") count++;
    if (type && type !== "All") count++;
    if (startDate && endDate) count++;
    if (minAmount) count++;
    if (maxAmount) count++;
    return count;
  };

  // Helper function to generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const { page, totalPages } = pagination;

    if (totalPages <= 7) {
      // Show all pages if total pages is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show ellipsis logic for more than 7 pages
      if (page <= 4) {
        // Show first 5 pages, ellipsis, and last page
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        // Show first page, ellipsis, and last 5 pages
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page, ellipsis, current page with neighbors, ellipsis, last page
        pages.push(1);
        pages.push("...");
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const activeFilterCount = countActiveFilters();
  const unacknowledgedCount = getUnacknowledgedNewCount();

  return (
    <div className="p-2 sm:p-4">
      {/* Header Section - Responsive */}
      <div className="flex flex-col space-y-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <h1 className="text-lg sm:text-xl font-bold">Transaction List</h1>
            {unacknowledgedCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-red-50 border border-red-200 rounded px-2 py-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-red-700 font-medium">
                    {unacknowledgedCount} new transaction
                    {unacknowledgedCount > 1 ? "s" : ""}
                  </span>
                  <button
                    onClick={handleMarkAsRead}
                    disabled={loadingStates.markAsRead}
                    className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Mark all transaction notifications as read"
                  >
                    {loadingStates.markAsRead ? "Marking..." : "Mark as read"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={fetchTransactions}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Add Transaction Report Generator */}
        <TransactionReportGenerator />

        {/* Search and Filter Section - Responsive */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by user name, email, mobile, transaction ID, or description..."
                value={searchInput}
                onChange={handleSearchInputChange}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  type="button"
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Filter Button */}
          <div className="relative">
            <button
              onClick={toggleFilterDropdown}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 2v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                />
              </svg>
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Filter Dropdown - Responsive */}
            {showFilterDropdown && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <form onSubmit={handleApplyFilters} className="p-4 space-y-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option === "All" ? "" : option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {typeOptions.map((option) => (
                        <option key={option} value={option === "All" ? "" : option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Amount Range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Amount
                      </label>
                      <input
                        type="number"
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                        placeholder="0"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Amount
                      </label>
                      <input
                        type="number"
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        placeholder="10000"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors text-sm"
                    >
                      Clear
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p>Loading transactions...</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">
          <p>{error}</p>
        </div>
      ) : (
        <>
          {/* Enhanced Transaction Table - Responsive */}
          <div className="bg-white border rounded shadow-sm overflow-hidden mb-4">
            {/* Mobile View */}
            <div className="block md:hidden">
              {transactions && transactions.length > 0 ? (
                transactions.map((txn) => (
                  <div
                    key={txn._id}
                    className={`p-4 border-b border-gray-200 cursor-pointer ${
                      shouldShowBlueDot(txn._id, txn.timestamp)
                        ? "bg-blue-50 border-l-4 border-l-blue-500"
                        : ""
                    }`}
                    onClick={() => handleTransactionClick(txn)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {shouldShowBlueDot(txn._id, txn.timestamp) && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900">
                            {txn.user?.fullName || "-"}
                          </p>
                          <p className="text-sm text-gray-600">
                            {txn.user?.mobileNo || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg text-gray-900">
                          ₹{parseFloat(txn.amount).toLocaleString()}
                        </p>
                        {shouldShowBlueDot(txn._id, txn.timestamp) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            New
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex gap-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            txn.status
                          )}`}
                        >
                          {txn.status}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeBadgeColor(
                            txn.type
                          )}`}
                        >
                          {txn.type}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <p>
                        {new Date(txn.timestamp).toLocaleDateString()} at{" "}
                        {new Date(txn.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <svg
                      className="w-12 h-12 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <p>No transactions found</p>
                  </div>
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-3 text-left font-semibold">User</th>
                    <th className="border p-3 text-left font-semibold">Email</th>
                    <th className="border p-3 text-left font-semibold">Mobile</th>
                    <th className="border p-3 text-left font-semibold">Amount</th>
                    <th className="border p-3 text-left font-semibold">Status</th>
                    <th className="border p-3 text-left font-semibold">Type</th>
                    <th className="border p-3 text-left font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions && transactions.length > 0 ? (
                    transactions.map((txn) => (
                      <tr
                        key={txn._id}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                          shouldShowBlueDot(txn._id, txn.timestamp)
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : ""
                        }`}
                        onClick={() => handleTransactionClick(txn)}
                      >
                        <td className="border p-3 relative">
                          {shouldShowBlueDot(txn._id, txn.timestamp) && (
                            <div className="absolute left-1 top-1/2 transform -translate-y-1/2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {txn.user?.fullName || "-"}
                            </span>
                            {shouldShowBlueDot(txn._id, txn.timestamp) && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                New
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="border p-3 text-gray-600">
                          {txn.user?.email || "-"}
                        </td>
                        <td className="border p-3 text-gray-600">
                          {txn.user?.mobileNo || "-"}
                        </td>
                        <td className="border p-3">
                          <span className="font-semibold text-gray-900">
                            ₹{parseFloat(txn.amount).toLocaleString()}
                          </span>
                        </td>
                        <td className="border p-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                              txn.status
                            )}`}
                          >
                            {txn.status}
                          </span>
                        </td>
                        <td className="border p-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(
                              txn.type
                            )}`}
                          >
                            {txn.type}
                          </span>
                        </td>
                        <td className="border p-3 text-gray-600">
                          <div className="text-sm">
                            {new Date(txn.timestamp).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(txn.timestamp).toLocaleTimeString()}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="border p-8 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <svg
                            className="w-12 h-12 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                          <p>No transactions found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Section - Responsive */}
            {pagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                {/* Mobile Pagination */}
                <div className="flex items-center justify-between md:hidden">
                  <div>
                    <p className="text-sm text-gray-700">
                      Page {pagination.page} of {pagination.totalPages}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className={`px-3 py-1 rounded text-sm ${
                        pagination.page === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-100 border"
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-3 py-1 rounded text-sm ${
                        pagination.page === pagination.totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-100 border"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>

                {/* Desktop Pagination */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {transactions.length > 0
                          ? (pagination.page - 1) * pagination.limit + 1
                          : 0}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )}
                      </span>{" "}
                      of <span className="font-medium">{pagination.total}</span>{" "}
                      results
                    </p>

                    {/* Page Size Selector */}
                    <select
                      value={pagination.limit}
                      onChange={(e) => handleLimitChange(e.target.value)}
                      className="border border-gray-300 rounded-md text-gray-600 text-sm py-1 px-2"
                    >
                      <option value="10">10 per page</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                    </select>
                  </div>

                  <div>
                    <nav className="relative z-0 inline-flex shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.page === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium 
                          ${
                            pagination.page === 1
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-white text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        <span className="sr-only">First Page</span>
                        &laquo;
                      </button>

                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className={`relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium 
                          ${
                            pagination.page === 1
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-white text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        <span className="sr-only">Previous</span>
                        &lsaquo;
                      </button>

                      {/* Page Numbers */}
                      {generatePageNumbers().map((pageNum, index) => (
                        <button
                          key={index}
                          onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                          disabled={pageNum === "..."}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium 
                            ${
                              pagination.page === pageNum
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : pageNum === "..."
                                ? "bg-white border-gray-300 text-gray-500 cursor-default"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                        >
                          {pageNum}
                        </button>
                      ))}

                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className={`relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium 
                          ${
                            pagination.page === pagination.totalPages
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-white text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        <span className="sr-only">Next</span>
                        &rsaquo;
                      </button>

                      <button
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.page === pagination.totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium 
                          ${
                            pagination.page === pagination.totalPages
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-white text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        <span className="sr-only">Last Page</span>
                        &raquo;
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionList;
                           